import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import SideMenu from '../components/layout/SideMenu';
import Alert from '../components/common/Alert';
import CategoryFormModal from '../components/products/CategoryFormModal';
import CategoryCard from '../components/products/CategoryCard';
import ViewToggle from '../components/products/ViewToggle';
import { useStorePageGuard } from '../hooks/useStorePageGuard';
import { Category, CreateCategoryPayload, UpdateCategoryPayload, ViewMode } from '../types/category';
import { getCategoriesByStore, createCategory, updateCategory, deleteCategory } from '../services/categoryService';
import { ApiErrorResponse } from '../services/authService';
import styles from './CategoriesPage.module.css';
import tableStyles from '../styles/shared/DataTable.module.css';
import cardGridStyles from '../styles/shared/CardGrid.module.css';
import layoutStyles from '../styles/layout.module.css';
import placeholderImage from '../assets/placeholder.png';

const CategoriesPage: React.FC = () => {
  const { loading, store, error } = useStorePageGuard({ requireAdmin: true });

  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'error'; isVisible: boolean }>({
    message: '',
    type: 'success',
    isVisible: false
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);

  useEffect(() => {
    if (alert.isVisible) {
      const timer = setTimeout(() => {
        setAlert(prev => ({ ...prev, isVisible: false }));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [alert.isVisible]);

  const showAlert = (message: string, type: 'success' | 'error') => {
    setAlert({ message, type, isVisible: true });
  };

  const hideAlert = () => {
    setAlert(prev => ({ ...prev, isVisible: false }));
  };

  useEffect(() => {
    if (loading || error || !store) return;
    const fetchCategories = async () => {
      try {
        setIsLoading(true);
        hideAlert();
        const fetchedCategories = await getCategoriesByStore(store.id);
        const displayCategories = fetchedCategories.map(category => ({
          ...category,
          name: category.name || category.category_name
        }));
        setCategories(displayCategories);
      } catch (err) {
        const apiError = err as ApiErrorResponse;
        showAlert(apiError.message || 'Failed to load categories.', 'error');
      } finally {
        setIsLoading(false);
      }
    };
    fetchCategories();
  }, [store, loading, error]);

  const handleAddCategory = () => {
    setEditingCategory(null);
    setIsModalOpen(true);
    hideAlert();
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setIsModalOpen(true);
    hideAlert();
  };

  const handleDeleteCategory = async (categoryId: number) => {
    if (window.confirm('Are you sure you want to delete this category? This might affect products using it.')) {
      hideAlert();
      try {
        const response = await deleteCategory(categoryId);
        setCategories(categories.filter(c => c.id !== categoryId));
        showAlert(response.message || 'Category deleted successfully!', 'success');
      } catch (err) {
        const apiError = err as ApiErrorResponse;
        showAlert(apiError.message || 'Failed to delete category.', 'error');
      }
    }
  };

  const handleFormSubmit = async (formData: CreateCategoryPayload | UpdateCategoryPayload) => {
    if (!store) return;
    hideAlert();
    setIsSubmitting(true);
    try {
      if (editingCategory) {
        const response = await updateCategory(editingCategory.id, formData as UpdateCategoryPayload);
        setCategories(categories.map(c => c.id === editingCategory.id ? response.category : c));
        showAlert(response.message || 'Category updated successfully!', 'success');
      } else {
        const response = await createCategory(store.id, formData as CreateCategoryPayload);
        setCategories([response.category, ...categories]);
        showAlert(response.message || 'Category created successfully!', 'success');
      }
      setIsModalOpen(false);
      setEditingCategory(null);
    } catch (err) {
      const apiError = err as ApiErrorResponse;
      showAlert(apiError.message || (editingCategory ? 'Failed to update category.' : 'Failed to create category.'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTableView = () => (
    <table className={tableStyles.dataTable}>
      <thead>
        <tr>
          <th>Image</th>
          <th>Name</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {categories.map((category) => (
          <tr key={category.id}>
            <td className={tableStyles.imageCell}>
              {category.image_url ? (
                <img 
                  src={category.image_url} 
                  alt={category.name || category.category_name}
                  className={tableStyles.tableImage}
                  onError={(e) => {
                    e.currentTarget.src = placeholderImage;
                  }}
                />
              ) : (
                <div className={tableStyles.tablePlaceholder}>📂</div>
              )}
            </td>
            <td>{category.name || category.category_name}</td>
            <td>
              <button onClick={() => handleEditCategory(category)} className={`${tableStyles.actionButton} ${tableStyles.editButton}`}>Edit</button>
              <button onClick={() => handleDeleteCategory(category.id)} className={`${tableStyles.actionButton} ${tableStyles.deleteButton}`}>Delete</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderCardView = () => (
    <div className={cardGridStyles.cardsGrid}>
      {categories.map((category) => (
        <CategoryCard
          key={category.id}
          category={category}
          onEdit={handleEditCategory}
          onDelete={handleDeleteCategory}
        />
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className={layoutStyles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <p>Loading store information...</p>
          </main>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={layoutStyles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <div className={styles.titleBar}>
              <h2 className={styles.title}>Categories</h2>
            </div>
            <p className={styles.errorMessage}>{error}</p>
          </main>
        </div>
      </div>
    );
  }

  if (isLoading && categories.length === 0) {
    return (
      <div className={layoutStyles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <p>Loading categories...</p>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className={layoutStyles.pageContainer}>
      <Header />
      <div className={styles.mainLayout}>
        <SideMenu />
        <main className={styles.contentArea}>
          <Alert 
            message={alert.message}
            type={alert.type}
            isVisible={alert.isVisible}
            onClose={hideAlert}
          />
          
          <div className={styles.titleBar}>
            <h2 className={styles.title}>Store Categories</h2>
            <div className={styles.controls}>
              <ViewToggle currentView={viewMode} onViewChange={setViewMode} />
              <button onClick={handleAddCategory} className={styles.addButton}>Add Category</button>
            </div>
          </div>

          {categories.length === 0 && !isLoading && !alert.isVisible && (
            <p>No categories found for this store. Click "Add Category" to create one.</p>
          )}

          {categories.length > 0 && (
            viewMode === 'table' ? renderTableView() : renderCardView()
          )}
          
          <CategoryFormModal
            isOpen={isModalOpen}
            onClose={() => { setIsModalOpen(false); setEditingCategory(null); hideAlert(); }}
            onSubmit={handleFormSubmit}
            initialData={editingCategory}
            isLoading={isSubmitting}
          />
        </main>
      </div>
    </div>
  );
};

export default CategoriesPage; 